import type { FC, MouseEvent, Ref } from 'react';
import { useCallback } from 'react';

import { Button } from '@/lib/base/buttons/button';

/**
 * RowActionButton — a hover-revealed icon button that sits in an overview
 * row's action cluster (edit pencil, clear, copy). One implementation so the
 * cluster's buttons can never drift apart in size, colour, or reveal timing.
 *
 * Stops click propagation so pressing it never also fires the surrounding
 * value region's own click (start editing / open the associated record).
 *
 * Revealed on hover (`group-hover`) and on keyboard focus anywhere within the
 * ancestor `group` (`group-focus-within`), and ALWAYS visible under
 * `pointer-coarse` (touch): there is no hover state on touch, so it's the
 * only discoverability signal. `pointer-coarse` is a real CSS media-query
 * variant (Tailwind v4), not user-agent sniffing. The caller's nearest
 * ancestor must carry the `group` class for the reveal to work.
 */
export interface RowActionButtonProps {
  icon: FC<{ className?: string }>;
  /** Accessible name — always an imperative phrase ("Edit Email"). */
  label: string;
  onClick: () => void;
  isDisabled?: boolean;
  /** The rendered button — a row hands focus back to it after an edit closes. */
  ref?: Ref<HTMLButtonElement | HTMLAnchorElement>;
}

export const ROW_ACTION_BUTTON_CLASS =
  'pointer-events-auto relative z-10 shrink-0 opacity-0 transition-opacity hover:text-fg-secondary group-hover:opacity-100 group-focus-within:opacity-100 pointer-coarse:opacity-100';

export function RowActionButton({
  icon,
  label,
  onClick,
  isDisabled,
  ref,
}: RowActionButtonProps) {
  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onClick();
    },
    [onClick]
  );
  return (
    <Button
      ref={ref}
      color='link-gray'
      size='xs'
      onClick={handleClick}
      isDisabled={isDisabled}
      aria-label={label}
      iconLeading={icon}
      // Icon size comes from the Button size ladder (`size-4` at `xs`) —
      // the previous size-3.5 double-override predates the base Button
      // scaling its icons per size.
      className={ROW_ACTION_BUTTON_CLASS}
    />
  );
}
